import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import { toast } from 'react-toastify';
import {
  searchCustomers,
  createOrder,
  createCustomer,
  searchShopProducts,
  addManualProduct,
} from '../services/orderService';
import {
  fetchCustomerAddresses,
  createCustomerAddress,
} from '../services/customerAddress';
import { fetchShops, createShop } from '../services/shopService';
import { fetchCategories as fetchShopCategories } from '../services/categoryService';
import {
  listCartItems,
  addCartItem,
  updateCartItem,
} from '../services/cartService';
import { fetchDeliveryCharges } from '../services/deliveryChargeService';
import { RefreshCw } from 'lucide-react';
import { fetchQuickOrderSuggestions } from '../services/quickOrderSuggestionService';
import { fetchShopProducts } from '../services/shopProductService';

const formatPriceDisplay = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const numericValue = typeof value === 'number' ? value : parseFloat(value);

  return Number.isFinite(numericValue) ? numericValue.toFixed(2) : '';
};

const VARIANT_UNIT_VALUE_KEYS = [
  'unit',
  'unitValue',
  'unitQty',
  'unitQuantity',
  'quantity',
  'qty',
];

const VARIANT_UNIT_NAME_KEYS = [
  'unitName',
  'unitLabel',
  'unitShort',
  'unitSymbol',
  'unitDisplay',
  'unitType',
  'unitMeasurement',
  'unit_measure',
  'unitAbbreviation',
  'unit_abbreviation',
];

const extractVariantUnit = (variant) => {
  const sources = [variant, variant?.productVariant];

  for (const source of sources) {
    if (!source || typeof source !== 'object') {
      continue;
    }

    const unitValue = VARIANT_UNIT_VALUE_KEYS.map((key) => source[key])
      .filter((val) => val !== null && val !== undefined && String(val).trim() !== '')
      .map((val) => String(val).trim())[0];

    const unitName = VARIANT_UNIT_NAME_KEYS.map((key) => source[key])
      .filter((val) => val !== null && val !== undefined && String(val).trim() !== '')
      .map((val) => String(val).trim())[0];

    if (unitValue && unitName) {
      return `${unitValue} ${unitName}`.trim();
    }

    if (unitName) {
      return unitName;
    }

    if (unitValue) {
      return unitValue;
    }
  }

  return '';
};

const mapCartResponse = (rec) =>
  rec.map((it) => ({
    cartItemId: it.id,
    shopId: it.shopId,
    shopName: it.shop?.name || it.shopName || '',
    productId: it.productId,
    productVariantId: it.productVariantId,
    productName: it.productName || it.product?.name || '',
    variantName: it.productVariant?.name || it.variantName || '',
    unitPrice: Number(it.unitPrice ?? 0),
    qty: Number(it.quantity ?? 1),
  }));

export default function CreateOrder() {
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [customerForm, setCustomerForm] = useState({ name: '', phone: '', address: '' });
  const [formErrors, setFormErrors] = useState({});

  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressForm, setAddressForm] = useState({ address: '', isDefault: '0' });
  const [addressFormErrors, setAddressFormErrors] = useState({});

  const [shopQuery, setShopQuery] = useState('');
  const [shopResults, setShopResults] = useState([]);
  const [activeShopSuggestionIndex, setActiveShopSuggestionIndex] = useState(-1);
  const [selectedShop, setSelectedShop] = useState(null);
  const [showShopForm, setShowShopForm] = useState(false);
  const [shopForm, setShopForm] = useState({
    name: '',
    phone: '',
    address: '',
    shopCategoryId: '',
  });
  const [shopFormErrors, setShopFormErrors] = useState({});
  const [shopList, setShopList] = useState([]);
  const [shopCategories, setShopCategories] = useState([]);

  const [productQuery, setProductQuery] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [productForm, setProductForm] = useState({ name: '', price: '' });
  const [productFormErrors, setProductFormErrors] = useState({});

  // item entry state
  const [productPrice, setProductPrice] = useState('');

  const [itemQty, setItemQty] = useState(1);
  const [items, setItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const customerInputRef = useRef(null);

  const [quickSuggestions, setQuickSuggestions] = useState([]);
  const [activeSuggestionCategoryId, setActiveSuggestionCategoryId] =
    useState(null);
  const [loadingQuickSuggestions, setLoadingQuickSuggestions] =
    useState(false);
  const [quickSuggestionSearch, setQuickSuggestionSearch] = useState('');
  const [quickSuggestionTab, setQuickSuggestionTab] = useState('default');
  const [selectedSuggestionShopId, setSelectedSuggestionShopId] =
    useState(null);
  const [shopWiseProducts, setShopWiseProducts] = useState({});
  const [loadingShopWiseProducts, setLoadingShopWiseProducts] =
    useState(false);
  const [shopWiseSearch, setShopWiseSearch] = useState('');

  const [deliveryCharges, setDeliveryCharges] = useState([]);
  const [kmInput, setKmInput] = useState('');
  const [deliveryChargeResult, setDeliveryChargeResult] = useState(null);
  const [deliveryCharge, setDeliveryCharge] = useState('22');
  const [orderNote, setOrderNote] = useState('');

  const refreshCartItems = useCallback(async (customerId) => {
    if (!customerId) return;
    try {
      const res = await listCartItems(customerId);
      const rec = res.data?.data || [];
      const mapped = mapCartResponse(rec);
      setItems(mapped);
      setShopList((prev) => {
        const existingIds = new Set(prev.map((s) => s.id));
        const additions = [];
        mapped.forEach((item) => {
          if (item.shopId != null && !existingIds.has(item.shopId)) {
            additions.push({ id: item.shopId, name: item.shopName || '' });
            existingIds.add(item.shopId);
          }
        });
        return additions.length ? [...prev, ...additions] : prev;
      });
    } catch (err) {
      /* ignore */
    }
  }, []);

  const loadQuickSuggestions = async () => {
    try {
      setLoadingQuickSuggestions(true);
      const res = await fetchQuickOrderSuggestions();
      const records =
        res.data?.data?.records || res.data?.data || res.data || [];
      setQuickSuggestions(records);
      setActiveSuggestionCategoryId((prev) => {
        if (!records.length) return null;
        if (prev && records.some((cat) => cat.id === prev)) {
          return prev;
        }
        return records[0].id;
      });
    } catch (err) {
      /* ignore */
    } finally {
      setLoadingQuickSuggestions(false);
    }
  };

  useEffect(() => {
    if (customerInputRef.current) {
      customerInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    setActiveShopSuggestionIndex(-1);
  }, [shopResults]);

  useEffect(() => {
    const loadShopCategories = async () => {
      try {
        const res = await fetchShopCategories();
        const records = res.data?.data?.records || res.data?.data || res.data || [];
        setShopCategories(records);
      } catch (err) {
        toast.error('Failed to load shop categories');
      }
    };

    loadShopCategories();
  }, []);

  useEffect(() => {
    if (!selectedProduct) {
      setSelectedVariant(null);
      setProductPrice('');
      return;
    }

    const variants = Array.isArray(selectedProduct.variants)
      ? selectedProduct.variants
      : [];

    if (variants.length === 0) {
      setSelectedVariant(null);
      setProductPrice(selectedProduct.price || '');
      return;
    }

    if (variants.length === 1) {
      const onlyVariant = variants[0];
      setSelectedVariant(onlyVariant);
      setProductPrice(onlyVariant?.price || '');
      return;
    }

    setSelectedVariant(null);
    setProductPrice('');
  }, [selectedProduct]);

  const shopMap = useMemo(() => {
    const map = new Map();
    shopList.forEach((shop) => {
      if (shop?.id != null) {
        map.set(shop.id, shop.name || '');
      }
    });
    return map;
  }, [shopList]);

  const groupedItems = items.reduce((acc, item) => {
    const key = item.shopId ?? '__no_shop__';
    if (!acc[key]) {
      const fallbackName =
        (item.shopId != null && shopMap.get(item.shopId)) ||
        item.shopName ||
        'Unassigned';
      acc[key] = {
        shopId: item.shopId ?? null,
        shopName: fallbackName,
        items: [],
      };
    }
    acc[key].items.push(item);
    return acc;
  }, {});

  const activeSuggestionCategory = useMemo(() => {
    if (!activeSuggestionCategoryId) return quickSuggestions[0] || null;
    return (
      quickSuggestions.find((cat) => cat.id === activeSuggestionCategoryId) ||
      null
    );
  }, [activeSuggestionCategoryId, quickSuggestions]);

  const quickSuggestionProducts = useMemo(() => {
    if (!activeSuggestionCategory || !Array.isArray(activeSuggestionCategory.products)) {
      return [];
    }
    return activeSuggestionCategory.products;
  }, [activeSuggestionCategory]);

  const parsePrice = useCallback((value) => {
    if (value === '' || value === null || value === undefined) {
      return null;
    }
    const numeric = Number(value);
    return Number.isNaN(numeric) ? null : numeric;
  }, []);

  const sortedShopList = useMemo(() => {
    return [...shopList].sort((a, b) => {
      const nameA = (a?.name || '').toLowerCase();
      const nameB = (b?.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [shopList]);

  const selectedSuggestionShop = useMemo(() => {
    if (!selectedSuggestionShopId) return null;
    return (
      sortedShopList.find((shop) => shop.id === selectedSuggestionShopId) ||
      null
    );
  }, [sortedShopList, selectedSuggestionShopId]);

  const shopWiseProductsForSelectedShop = useMemo(() => {
    if (!selectedSuggestionShopId) return [];
    const list = shopWiseProducts[selectedSuggestionShopId];
    return Array.isArray(list) ? list : [];
  }, [shopWiseProducts, selectedSuggestionShopId]);

  const filteredQuickSuggestionProducts = useMemo(() => {
    if (!Array.isArray(quickSuggestionProducts)) {
      return [];
    }

    const query = quickSuggestionSearch.trim().toLowerCase();
    if (!query) {
      return quickSuggestionProducts;
    }

    return quickSuggestionProducts.filter((prod) => {
      const name = (prod?.name || '').toLowerCase();
      return name.includes(query);
    });
  }, [quickSuggestionProducts, quickSuggestionSearch]);

  const filteredShopWiseProducts = useMemo(() => {
    const list = shopWiseProductsForSelectedShop;
    if (!Array.isArray(list)) {
      return [];
    }

    const query = shopWiseSearch.trim().toLowerCase();
    if (!query) {
      return list;
    }

    return list.filter((prod) =>
      (prod.name || '').toLowerCase().includes(query),
    );
  }, [shopWiseProductsForSelectedShop, shopWiseSearch]);

  useEffect(() => {
    setQuickSuggestionSearch('');
  }, [activeSuggestionCategoryId]);

  useEffect(() => {
    if (quickSuggestionTab !== 'shop') return;
    if (selectedShop?.id) {
      setSelectedSuggestionShopId(selectedShop.id);
      return;
    }
    if (!selectedSuggestionShopId && sortedShopList.length) {
      setSelectedSuggestionShopId(sortedShopList[0].id);
    }
  }, [quickSuggestionTab, selectedShop, sortedShopList, selectedSuggestionShopId]);

  useEffect(() => {
    setShopWiseSearch('');
  }, [selectedSuggestionShopId]);

  const fetchProductsForShop = useCallback(
    async (shopId, { force = false } = {}) => {
      if (!shopId) return;
      if (!force && shopWiseProducts[shopId]) {
        return;
      }
      setLoadingShopWiseProducts(true);
      try {
        const res = await fetchShopProducts(shopId, {
          limit: 200,
          page: 1,
          sortBy: 'name',
          sortOrder: 'ASC',
        });
        const records =
          res.data?.data?.records || res.data?.data?.rows || res.data?.data || res.data || [];
        const normalized = records.map((record) => {
          const baseProduct = record.product || record;
          const rawVariants =
            Array.isArray(record.variants) && record.variants.length
              ? record.variants
              : Array.isArray(baseProduct?.variants)
                ? baseProduct.variants
                : [];
          const variants = rawVariants.map((variant) => {
            const variantId =
              variant.productVariantId ||
              variant.id ||
              variant.productVariant?.id ||
              null;
            const priceValue =
              variant.price ??
              variant.sellingPrice ??
              variant.mrp ??
              (variant.productVariant ? variant.productVariant.price : null);
            return {
              id: variantId,
              productVariantId: variant.productVariantId || variantId,
              name:
                variant.name ||
                variant.productVariant?.name ||
                (variantId ? `Variant ${variantId}` : 'Variant'),
              price: parsePrice(priceValue),
              unit: extractVariantUnit(variant),
            };
          });
          const priceValue =
            record.price ??
            baseProduct?.price ??
            baseProduct?.defaultPrice ??
            baseProduct?.sellingPrice ??
            baseProduct?.mrp ??
            null;
          const defaultPrice = parsePrice(priceValue);
          const productId =
            record.productId ??
            baseProduct?.id ??
            record.id ??
            null;
          return {
            shopProductId: record.id ?? null,
            productId,
            name: baseProduct?.name || record.name || 'Unnamed product',
            price: defaultPrice,
            defaultPrice,
            variants,
          };
        });
        setShopWiseProducts((prev) => ({ ...prev, [shopId]: normalized }));
      } catch (err) {
        toast.error('Failed to load shop products');
      } finally {
        setLoadingShopWiseProducts(false);
      }
    },
    [parsePrice, shopWiseProducts],
  );

  useEffect(() => {
    if (quickSuggestionTab !== 'shop') return;
    if (!selectedSuggestionShopId) return;
    fetchProductsForShop(selectedSuggestionShopId);
  }, [quickSuggestionTab, selectedSuggestionShopId, fetchProductsForShop]);

  useEffect(() => {
    const fetch = async () => {
      if (customerQuery.length < 2 || selectedCustomer) {
        setCustomerResults([]);
        return;
      }
      try {
        const res = await searchCustomers(customerQuery);
        const rec =
          res.data?.data?.records || res.data?.data || res.data || [];
        setCustomerResults(rec);
      } catch (err) {
        console.error(err);
      }
    };
    fetch();
  }, [customerQuery, selectedCustomer]);

  useEffect(() => {
    const fetch = async () => {
      if (shopQuery.length < 2 || selectedShop) {
        setShopResults([]);
        return;
      }
      try {
        const res = await fetchShops({ search: shopQuery, limit: 10 });
        const rec = res.data?.data?.records || res.data?.data || [];
        setShopResults(rec);
      } catch (err) {
        /* ignore */
      }
    };
    fetch();
  }, [shopQuery, selectedShop]);

  useEffect(() => {
    const fetch = async () => {
      if (!selectedShop || productQuery.length < 2) {
        setProductResults([]);
        return;
      }
      try {
        const res = await searchShopProducts(selectedShop.id, productQuery);
        const rec =
          res.data?.data?.records || res.data?.data || res.data || [];
        // API returns shop product entries with nested product info
        // Preserve variants from shop product so variant selection works
        const products = rec.map((sp) => {
          const prod = sp.product ? { ...sp.product } : { ...sp };
          if (sp.variants) {
            prod.variants = sp.variants.map((v) => ({
              ...v,
              name:
                v.name ||
                (v.productVariant ? v.productVariant.name : undefined) ||
                `Variant ${v.productVariantId}`,
            }));
          }
          return prod;
        });
        setProductResults(products);
      } catch (err) {
        /* ignore */
      }
    };
    fetch();
  }, [productQuery, selectedShop]);

  useEffect(() => {
    setProductResults([]);
    setProductQuery('');
  }, [selectedShop]);

  useEffect(() => {
    const loadShops = async () => {
      try {
        const res = await fetchShops({ limit: 200, page: 1 });
        const rec = res.data?.data?.records || res.data?.data || res.data || [];
        setShopList(rec);
      } catch (err) {
        /* ignore */
      }
    };
    loadShops();
  }, []);

  useEffect(() => {
    loadQuickSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedCustomer) {
      setItems([]);
      return;
    }
    refreshCartItems(selectedCustomer.id);
  }, [refreshCartItems, selectedCustomer]);

  useEffect(() => {
    if (!selectedCustomer) {
      setCustomerAddresses([]);
      setSelectedAddress(null);
      return;
    }
    const loadAddresses = async () => {
      try {
        const res = await fetchCustomerAddresses(selectedCustomer.id);
        const rec = res.data?.data?.records || res.data?.data || res.data || [];
        setCustomerAddresses(rec);
        const def = rec.find((a) => a.isDefault) || rec[0] || null;
        setSelectedAddress(def);
      } catch (err) {
        /* ignore */
      }
    };
    loadAddresses();
  }, [selectedCustomer]);

  useEffect(() => {
    const loadCharges = async () => {
      try {
        const res = await fetchDeliveryCharges({
          sortBy: 'minKilometer',
          sortOrder: 'ASC',
          limit: 1000,
        });
        const rec = res.data?.data?.records || res.data?.data || [];
        setDeliveryCharges(rec.filter((c) => c.isActive));
      } catch (err) {
        /* ignore */
      }
    };
    loadCharges();
  }, []);

  useEffect(() => {
    const km = parseFloat(kmInput);
    if (isNaN(km)) {
      setDeliveryChargeResult(null);
      return;
    }
    const charge = deliveryCharges.find(
      (c) =>
        km >= c.minKilometer &&
        (c.maxKilometer === null || km <= c.maxKilometer),
    );
    const amount = charge ? Number(charge.charge) : null;
    setDeliveryChargeResult(
      typeof amount === 'number' && !isNaN(amount) ? amount : null,
    );
  }, [kmInput, deliveryCharges]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCustomer) {
      toast.error('Select a customer');
      return;
    }
    if (!selectedAddress) {
      toast.error('Select a delivery address');
      return;
    }
    if (items.length === 0) {
      toast.error('Add at least one item');
      return;
    }
    try {
      setSubmitting(true);
      await createOrder(selectedCustomer.id, {
        items: items.map((i) => ({
          shopId: i.shopId,
          productId: i.productId,
          productVariantId: i.productVariantId,
          productName: i.productName,
          quantity: i.qty,
          unitPrice: i.unitPrice,
        })),
        deliveryCharge: Number(deliveryCharge) || 0,
        note: orderNote,
        customerAddressId: selectedAddress.id,
      });
      toast.success('Order created');
      setSelectedCustomer(null);
      setCustomerQuery('');
      setItems([]);
      setSelectedShop(null);
      setShopQuery('');
      setSelectedProduct(null);
      setProductQuery('');
      setDeliveryCharge('');
      setOrderNote('');
      setCustomerAddresses([]);
      setSelectedAddress(null);
    } catch (err) {
      console.error(err);
      toast.error('Failed to create order');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCustomerSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!customerForm.name) errors.name = 'Name is required';
    if (!customerForm.phone) errors.phone = 'Phone is required';
    if (!customerForm.address) errors.address = 'Address is required';
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }
    try {
      const res = await createCustomer({
        name: customerForm.name,
        phone: customerForm.phone,
      });
      const cust = res.data?.data || res.data;
      if (cust?.id) {
        const addrRes = await createCustomerAddress(cust.id, {
          address: customerForm.address,
          isDefault: true,
        });
        const addr = addrRes.data?.data || addrRes.data;
        setSelectedCustomer(cust);
        setCustomerAddresses([addr]);
        setSelectedAddress(addr);
        setCustomerQuery('');
      }
      setShowCustomerForm(false);
      setCustomerForm({ name: '', phone: '', address: '' });
      setFormErrors({});
    } catch (err) {
      console.error(err);
      toast.error('Failed to create customer');
    }
  };

  const handleShopSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!shopForm.name) errors.name = 'Name is required';
    if (!shopForm.phone) errors.phone = 'Phone is required';
    if (!shopForm.address) errors.address = 'Address is required';
    if (!shopForm.shopCategoryId) errors.shopCategoryId = 'Category is required';
    if (Object.keys(errors).length) {
      setShopFormErrors(errors);
      return;
    }
    try {
      const res = await createShop({
        name: shopForm.name,
        phone: shopForm.phone,
        address: shopForm.address,
        shopCategoryId: Number(shopForm.shopCategoryId),
      });
      const shop = res.data?.data || res.data;
      if (shop?.id) {
        setSelectedShop(shop);
        setShopQuery('');
        setShopList((prev) => {
          if (prev.some((s) => s.id === shop.id)) {
            return prev.map((s) => (s.id === shop.id ? shop : s));
          }
          return [shop, ...prev];
        });
      }
      setShowShopForm(false);
      setShopForm({ name: '', phone: '', address: '', shopCategoryId: '' });
      setShopFormErrors({});
    } catch (err) {
      toast.error('Failed to create shop');
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!productForm.name) errors.name = 'Name is required';
    if (!productForm.price) errors.price = 'Price is required';
    if (Object.keys(errors).length) {
      setProductFormErrors(errors);
      return;
    }
    if (!selectedShop) {
      toast.error('Select a shop first');
      return;
    }
    try {
      const res = await addManualProduct(selectedShop.id, {
        name: productForm.name,
        price: productForm.price,
      });
      const prod = res.data?.data || res.data;
      if (prod?.id || prod?.product) {
        const productObj = prod.product || prod;
        setSelectedProduct(productObj);
        setProductQuery('');
      }
      setShowProductForm(false);
      setProductForm({ name: '', price: '' });
      setProductFormErrors({});
    } catch (err) {
      toast.error('Failed to add product');
    }
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!addressForm.address) errors.address = 'Address is required';
    if (Object.keys(errors).length) {
      setAddressFormErrors(errors);
      return;
    }
    if (!selectedCustomer) return;
    try {
      const res = await createCustomerAddress(selectedCustomer.id, {
        address: addressForm.address,
        isDefault: addressForm.isDefault === '1',
      });
      const addr = res.data?.data || res.data;
      const listRes = await fetchCustomerAddresses(selectedCustomer.id);
      const rec =
        listRes.data?.data?.records || listRes.data?.data || listRes.data || [];
      setCustomerAddresses(rec);
      const selected =
        rec.find((a) => a.id === addr.id) ||
        rec.find((a) => a.isDefault) ||
        rec[0] ||
        null;
      setSelectedAddress(selected);
      setShowAddressForm(false);
      setAddressForm({ address: '', isDefault: '0' });
      setAddressFormErrors({});
    } catch (err) {
      toast.error('Failed to add address');
    }
  };

  const handleVariantSelect = (variantId) => {
    if (!selectedProduct) return;
    const variant = selectedProduct.variants.find(
      (v) => String(v.productVariantId || v.id) === variantId,
    );
    setSelectedVariant(variant || null);
    setProductPrice(variant?.price || '');
  };

  const clearItemForm = () => {
    setSelectedProduct(null);
    setSelectedVariant(null);
    setProductQuery('');
    setProductPrice('');
    setItemQty(1);
  };

  const addItem = async () => {
    if (!selectedCustomer) {
      toast.error('Select customer');
      return;
    }
    const name = selectedProduct ? selectedProduct.name : productQuery.trim();
    if (!name) {
      toast.error('Enter product name');
      return;
    }
    if (
      selectedProduct &&
      selectedProduct.variants &&
      selectedProduct.variants.length > 0 &&
      !selectedVariant
    ) {
      toast.error('Select variant');
      return;
    }
    if (!productPrice) {
      toast.error('Enter price');
      return;
    }
    const price = Number(productPrice) || 0;
    try {
      const payload = selectedProduct
        ? {
            productId: selectedProduct.id,
            productVariantId:
              selectedVariant?.id || selectedVariant?.productVariantId || null,
            productName: name,
            quantity: Number(itemQty) || 1,
            unitPrice: price,
          }
        : {
            productName: name,
            quantity: Number(itemQty) || 1,
            unitPrice: price,
          };

      if (selectedShop?.id) {
        payload.shopId = selectedShop.id;
      }

      await addCartItem(selectedCustomer.id, payload);
      await refreshCartItems(selectedCustomer.id);
      clearItemForm();
    } catch (err) {
      toast.error('Failed to add item');
    }
  };

  const handleQuickProductSelect = (product) => {
    if (!product) return;
    setSelectedProduct(product);
    setItemQty(1);
    if (!product.variants || product.variants.length === 0) {
      setProductPrice(product.price || '');
    } else if (product.variants.length === 1) {
      const onlyVariant = product.variants[0];
      setSelectedVariant(onlyVariant);
      setProductPrice(onlyVariant?.price || '');
    } else {
      toast.info('Select a variant before adding the item.');
    }
  };

  const handleSuggestionClick = async (suggestion) => {
    if (!suggestion) return;
    if (!selectedCustomer) {
      toast.error('Select customer first');
      return;
    }

    const defaultPrice =
      suggestion.defaultPrice != null
        ? Number(suggestion.defaultPrice)
        : null;

    if (defaultPrice == null || Number.isNaN(defaultPrice)) {
      setSelectedProduct(null);
      setProductQuery(suggestion.name);
      setProductPrice('');
      toast.info('Enter a price and click Add Item to add this suggestion.');
      return;
    }

    const payload = {
      productName: suggestion.name,
      quantity: 1,
      unitPrice: defaultPrice,
    };

    if (selectedShop?.id) {
      payload.shopId = selectedShop.id;
    }

    try {
      await addCartItem(selectedCustomer.id, payload);
      await refreshCartItems(selectedCustomer.id);
      clearItemForm();
      toast.success('Item added to cart');
    } catch (err) {
      toast.error('Failed to add item');
    }
  };

  const handleSuggestionShopSelect = (shopId) => {
    const numericId = shopId ? Number(shopId) : null;
    if (!numericId) return;
    setSelectedSuggestionShopId(numericId);
    const shop =
      sortedShopList.find((s) => s.id === numericId) ||
      shopList.find((s) => s.id === numericId) ||
      null;
    if (shop) {
      setSelectedShop(shop);
      setShopList((prev) => {
        if (prev.some((s) => s.id === shop.id)) {
          return prev;
        }
        return [...prev, shop];
      });
    }
  };

  const handleShopWiseProductClick = async (product) => {
    if (!product) return;
    if (!selectedCustomer) {
      toast.error('Select customer first');
      return;
    }
    if (!selectedSuggestionShopId) {
      toast.error('Select a shop first');
      return;
    }

    const suggestionShop =
      sortedShopList.find((s) => s.id === selectedSuggestionShopId) ||
      shopList.find((s) => s.id === selectedSuggestionShopId) ||
      null;
    if (suggestionShop) {
      setSelectedShop(suggestionShop);
      setShopList((prev) => {
        if (prev.some((s) => s.id === suggestionShop.id)) {
          return prev;
        }
        return [...prev, suggestionShop];
      });
    }

    const variants = Array.isArray(product.variants) ? product.variants : [];
    if (variants.length > 1) {
      setSelectedProduct({
        id: product.productId,
        name: product.name,
        price: product.price,
        variants,
      });
      setSelectedVariant(null);
      setProductPrice('');
      toast.info('Select a variant from the Add Item form to continue.');
      return;
    }

    const chosenVariant = variants.length === 1 ? variants[0] : null;
    const priceSource =
      chosenVariant?.price != null ? chosenVariant.price : product.price;
    const unitPrice = parsePrice(priceSource);

    if (unitPrice == null) {
      setSelectedProduct({
        id: product.productId,
        name: product.name,
        price: product.price,
        variants,
      });
      setSelectedVariant(chosenVariant || null);
      setProductPrice('');
      toast.info('Enter a price in the Add Item form before adding.');
      return;
    }

    const payload = {
      productId: product.productId || null,
      productVariantId:
        chosenVariant?.productVariantId || chosenVariant?.id || null,
      productName: product.name,
      quantity: 1,
      unitPrice,
      shopId: selectedSuggestionShopId,
    };

    try {
      await addCartItem(selectedCustomer.id, payload);
      await refreshCartItems(selectedCustomer.id);
      clearItemForm();
      toast.success('Item added to cart');
    } catch (err) {
      toast.error('Failed to add item');
    }
  };

  const updateItemQty = (index, qty) => {
    const newQty = Number(qty) || 1;
    const item = items[index];
    setItems((prev) =>
      prev.map((it, i) => (i === index ? { ...it, qty: newQty } : it)),
    );
    if (item?.cartItemId && selectedCustomer) {
      updateCartItem(selectedCustomer.id, item.cartItemId, { quantity: newQty }).catch(
        () => {}
      );
    }
  };

  const updateItemName = (index, value) => {
    const nextName = value;
    const item = items[index];
    setItems((prev) =>
      prev.map((it, i) => (i === index ? { ...it, productName: nextName } : it)),
    );
    if (item?.cartItemId && selectedCustomer) {
      const trimmed = nextName.trim();
      if (!trimmed) return;
      updateCartItem(selectedCustomer.id, item.cartItemId, {
        productName: trimmed,
      }).catch(() => {});
    }
  };

  const updateItemPrice = (index, price) => {
    const newPrice = Number(price) || 0;
    const item = items[index];
    setItems((prev) =>
      prev.map((it, i) => (i === index ? { ...it, unitPrice: newPrice } : it)),
    );
    if (item?.cartItemId && selectedCustomer) {
      updateCartItem(selectedCustomer.id, item.cartItemId, { unitPrice: newPrice }).catch(
        () => {}
      );
    }
  };

  const removeItem = (index) => {
    const item = items[index];
    setItems((prev) => prev.filter((_, i) => i !== index));
    if (item?.cartItemId && selectedCustomer) {
      updateCartItem(selectedCustomer.id, item.cartItemId, { status: 2 }).catch(
        () => {}
      );
    }
  };

  const handleItemShopChange = (index, value) => {
    const currentItem = items[index];
    const normalizedShopId = value ? Number(value) : null;
    const selected =
      normalizedShopId != null
        ? shopList.find((shop) => shop.id === normalizedShopId) || null
        : null;

    setItems((prev) =>
      prev.map((it, i) =>
        i === index
          ? {
              ...it,
              shopId: normalizedShopId,
              shopName: selected ? selected.name : '',
            }
          : it,
      ),
    );

    if (currentItem?.cartItemId && selectedCustomer) {
      updateCartItem(selectedCustomer.id, currentItem.cartItemId, {
        shopId: normalizedShopId,
      }).catch(() => {});
    }
  };

  const handleFormKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
      e.preventDefault();
    }
  };

  const handleShopSelect = (shop) => {
    if (!shop) return;
    setSelectedShop(shop);
    setShopResults([]);
    setShopList((prev) => {
      if (prev.some((existing) => existing.id === shop.id)) {
        return prev;
      }
      return [...prev, shop];
    });
  };

  const handleShopInputKeyDown = (e) => {
    if (shopResults.length === 0) {
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveShopSuggestionIndex((prev) => {
        const nextIndex = prev + 1;
        if (nextIndex >= shopResults.length) {
          return 0;
        }
        return nextIndex;
      });
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveShopSuggestionIndex((prev) => {
        const nextIndex = prev - 1;
        if (nextIndex < 0) {
          return shopResults.length - 1;
        }
        return nextIndex;
      });
      return;
    }

    if (e.key === 'Enter' && activeShopSuggestionIndex >= 0) {
      e.preventDefault();
      e.stopPropagation();
      const shop = shopResults[activeShopSuggestionIndex];
      handleShopSelect(shop);
    }
  };

  return (
    <LayoutWithSidebar>
      <div className="p-4 flex flex-col md:flex-row md:space-x-4">
        <div className="w-full md:w-7/12 lg:w-3/5">
          <h1 className="text-2xl font-bold mb-4">Create Order</h1>
          <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Customer</label>
            <div className="flex space-x-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  ref={customerInputRef}
                  className="w-full border rounded px-2 py-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Search by name or phone"
                  value={selectedCustomer ? selectedCustomer.name : customerQuery}
                  onChange={(e) => {
                    setSelectedCustomer(null);
                    setCustomerQuery(e.target.value);
                  }}
                />
                {customerResults.length > 0 && (
                  <ul className="absolute z-10 bg-white border w-full mt-1 max-h-40 overflow-y-auto dark:bg-gray-700 dark:border-gray-600">
                    {customerResults.map((c) => (
                      <li
                        key={c.id}
                        className="px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer"
                        onClick={() => {
                          setSelectedCustomer(c);
                          setCustomerResults([]);
                        }}
                      >
                        {c.name} ({c.phone})
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                type="button"
                className="px-2 py-1 bg-green-600 text-white rounded"
                onClick={() => setShowCustomerForm(true)}
              >
                Add
              </button>
            </div>
          </div>
          {selectedCustomer && (
            <div>
              <label className="block text-sm font-medium mb-1">Address</label>
              <div className="flex space-x-2">
                <select
                  className="w-full border rounded px-2 py-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  value={selectedAddress ? selectedAddress.id : ''}
                  onChange={(e) => {
                    const addr = customerAddresses.find(
                      (a) => a.id === Number(e.target.value),
                    );
                    setSelectedAddress(addr || null);
                  }}
                >
                  {customerAddresses.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.address} {a.isDefault ? '(Default)' : ''}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="px-2 py-1 bg-green-600 text-white rounded"
                  onClick={() => setShowAddressForm(true)}
                >
                  Add
                </button>
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Shop</label>
            <div className="flex space-x-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Search shop"
                  value={selectedShop ? selectedShop.name : shopQuery}
                  onChange={(e) => {
                    setSelectedShop(null);
                    setShopQuery(e.target.value);
                    setActiveShopSuggestionIndex(-1);
                  }}
                  onKeyDown={handleShopInputKeyDown}
                />
                {shopResults.length > 0 && (
                  <ul className="absolute z-10 bg-white border w-full mt-1 max-h-40 overflow-y-auto dark:bg-gray-700 dark:border-gray-600">
                    {shopResults.map((s, index) => {
                      const isActive = index === activeShopSuggestionIndex;
                      return (
                        <li
                          key={s.id}
                          className={`px-2 py-1 cursor-pointer ${
                            isActive
                              ? 'bg-gray-100 dark:bg-gray-600'
                              : 'hover:bg-gray-100 dark:hover:bg-gray-600'
                          }`}
                          onMouseEnter={() => setActiveShopSuggestionIndex(index)}
                          onMouseLeave={() => setActiveShopSuggestionIndex(-1)}
                          onClick={() => handleShopSelect(s)}
                        >
                          {s.name}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <button
                type="button"
                className="px-2 py-1 bg-green-600 text-white rounded"
                onClick={() => setShowShopForm(true)}
              >
                Add
              </button>
            </div>
          </div>
          <div className="border rounded p-4 space-y-4 dark:border-gray-700">
            <h3 className="font-semibold">Add Item</h3>
            {!selectedShop && (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Items added without a shop will be marked as Unassigned.
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Search or enter product..."
                  value={selectedProduct ? selectedProduct.name : productQuery}
                  onChange={(e) => {
                    setSelectedProduct(null);
                    setProductQuery(e.target.value);
                  }}
                />
                {productResults.length > 0 && (
                  <ul className="absolute z-10 bg-white border w-full mt-1 max-h-40 overflow-y-auto dark:bg-gray-700 dark:border-gray-600">
                    {productResults.map((p) => (
                      <li
                        key={p.id}
                        className="px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer"
                        onClick={() => {
                          setSelectedProduct(p);
                          setProductResults([]);
                        }}
                      >
                        {p.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {selectedProduct &&
                selectedProduct.variants &&
                selectedProduct.variants.length > 0 && (
                  <select
                    className="border rounded px-2 py-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    value={
                      selectedVariant
                        ? String(
                            selectedVariant.productVariantId ||
                              selectedVariant.id,
                          )
                        : ''
                    }
                    onChange={(e) => handleVariantSelect(e.target.value)}
                  >
                    <option value="">Select variant</option>
                    {selectedProduct.variants.map((v) => (
                      <option
                        key={v.productVariantId || v.id}
                        value={v.productVariantId || v.id}
                      >
                        {v.name}
                      </option>
                    ))}
                  </select>
                )}
              <input
                type="number"
                className="w-24 border rounded px-2 py-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Price"
                value={productPrice}
                onChange={(e) => setProductPrice(e.target.value)}
              />
              <input
                type="number"
                min="1"
                className="w-20 border rounded px-2 py-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Quantity"
                value={itemQty}
                onChange={(e) => setItemQty(e.target.value)}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                className="px-4 py-2 bg-gray-200 rounded dark:bg-gray-700 dark:text-white"
                onClick={clearItemForm}
              >
                Clear
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-indigo-600 text-white rounded"
                onClick={addItem}
              >
                Add Item
              </button>
            </div>
          </div>

            {items.length > 0 && (
              <div className="mt-4">
                <h3 className="font-semibold mb-2">Items</h3>
                <table className="min-w-full text-sm bg-white dark:bg-gray-800">
                  <thead>
                    <tr className="text-left bg-gray-100 dark:bg-gray-700">
                      <th className="px-2 py-1">Shop</th>
                      <th className="px-2 py-1">Item</th>
                      <th className="px-2 py-1">Variant</th>
                      <th className="px-2 py-1">Unit Price</th>
                      <th className="px-2 py-1">Qty</th>
                      <th className="px-2 py-1">Total</th>
                      <th className="px-2 py-1">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, idx) => (
                      <tr key={idx} className="border-t dark:border-gray-700">
                        <td className="px-2 py-1">
                          <select
                            className="w-full border rounded px-2 py-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            value={it.shopId != null ? String(it.shopId) : ''}
                            onChange={(e) => handleItemShopChange(idx, e.target.value)}
                          >
                            <option value="">Unassigned</option>
                            {it.shopId != null &&
                              !shopList.some((shop) => shop.id === it.shopId) && (
                                <option value={it.shopId}>
                                  {it.shopName || `Shop #${it.shopId}`}
                                </option>
                              )}
                            {shopList.map((shop) => (
                              <option key={shop.id} value={shop.id}>
                                {shop.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 py-1">
                          <input
                            type="text"
                            value={it.productName}
                            onChange={(e) => updateItemName(idx, e.target.value)}
                            className="w-full border rounded px-1 py-0.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          />
                        </td>
                        <td className="px-2 py-1">{it.variantName}</td>
                        <td className="px-2 py-1">
                          <input
                            type="number"
                            min="0"
                            value={it.unitPrice}
                            onChange={(e) => updateItemPrice(idx, e.target.value)}
                            className="w-20 border rounded px-1 py-0.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <input
                            type="number"
                            min="1"
                            value={it.qty}
                            onChange={(e) => updateItemQty(idx, e.target.value)}
                            className="w-16 border rounded px-1 py-0.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          />
                        </td>
                        <td className="px-2 py-1">
                          {(it.unitPrice * it.qty).toFixed(2)}
                        </td>
                        <td className="px-2 py-1">
                          <button
                            type="button"
                            className="text-red-600"
                            onClick={() => removeItem(idx)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                    <tr className="font-semibold border-t dark:border-gray-700">
                      <td colSpan="4" className="px-2 py-1 text-right">
                        Grand Total
                      </td>
                      <td className="px-2 py-1">
                        {items
                          .reduce((sum, i) => sum + i.unitPrice * i.qty, 0)
                          .toFixed(2)}
                      </td>
                      <td />
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
            <div>
              <label className="block font-medium mb-1">Delivery Charge</label>
              <input
                type="number"
                className="w-full border rounded px-2 py-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                value={deliveryCharge}
                onChange={(e) => setDeliveryCharge(e.target.value)}
              />
            </div>
            <div>
              <label className="block font-medium mb-1">Order Notes</label>
              <textarea
                className="w-full border rounded px-2 py-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                value={orderNote}
                onChange={(e) => setOrderNote(e.target.value)}
              />
            </div>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 bg-indigo-600 text-white rounded"
          >
            {submitting ? 'Saving...' : 'Create Order'}
          </button>
          </form>
        </div>
        <div className="w-full md:w-5/12 lg:w-2/5 mt-4 md:mt-0 space-y-4">
          <div className="border rounded p-2 dark:border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Quick Suggestions</h3>
              <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-300">
                {loadingQuickSuggestions ? (
                  <span>Loading...</span>
                ) : (
                  quickSuggestions.length > 0 && (
                    <button
                      type="button"
                      className={`hover:underline ${
                        loadingQuickSuggestions
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                      }`}
                      onClick={loadQuickSuggestions}
                      disabled={loadingQuickSuggestions}
                    >
                      Refresh
                    </button>
                  )
                )}
              </div>
            </div>
            <div className="flex border-b border-gray-200 dark:border-gray-700 text-sm">
              <button
                type="button"
                onClick={() => setQuickSuggestionTab('default')}
                className={`px-3 py-1.5 transition-colors ${
                  quickSuggestionTab === 'default'
                    ? 'border-b-2 border-indigo-600 text-indigo-600 font-medium'
                    : 'text-gray-600 dark:text-gray-300'
                }`}
              >
                Default Products
              </button>
              <button
                type="button"
                onClick={() => setQuickSuggestionTab('shop')}
                className={`ml-2 px-3 py-1.5 transition-colors ${
                  quickSuggestionTab === 'shop'
                    ? 'border-b-2 border-indigo-600 text-indigo-600 font-medium'
                    : 'text-gray-600 dark:text-gray-300'
                }`}
              >
                Shop Wise
              </button>
            </div>
            {quickSuggestionTab === 'default' ? (
              quickSuggestions.length === 0 ? (
                <p className="mt-3 text-sm text-gray-500 dark:text-gray-300">
                  Configure quick order categories to see suggestions here.
                </p>
              ) : (
                <div className="mt-3 space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {quickSuggestions.map((cat) => {
                      const isActive = activeSuggestionCategory
                        ? activeSuggestionCategory.id === cat.id
                        : false;
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => setActiveSuggestionCategoryId(cat.id)}
                          className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                            isActive
                              ? 'bg-indigo-600 text-white border-indigo-600'
                              : 'bg-white text-gray-700 border-gray-300 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600'
                          }`}
                        >
                          {cat.name}
                        </button>
                      );
                    })}
                  </div>
                  <input
                    type="text"
                    value={quickSuggestionSearch}
                    onChange={(e) => setQuickSuggestionSearch(e.target.value)}
                    placeholder="Search products"
                    className="w-full border rounded px-3 py-2 text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                  />
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                    {quickSuggestionProducts.length === 0 ? (
                      <div className="text-sm text-gray-500 dark:text-gray-300">
                        No products in this category yet.
                      </div>
                    ) : filteredQuickSuggestionProducts.length === 0 ? (
                      <div className="text-sm text-gray-500 dark:text-gray-300">
                        No products match your search.
                      </div>
                    ) : (
                      filteredQuickSuggestionProducts.map((prod) => (
                        <button
                          key={`quick-${prod.id || prod.name}`}
                          type="button"
                          onClick={() => handleSuggestionClick(prod)}
                          className="w-full text-left border rounded px-3 py-2 hover:bg-indigo-50 dark:hover:bg-gray-700 dark:border-gray-600"
                        >
                          <div className="flex items-center justify-between">
                            <div className="font-medium">{prod.name}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-300">
                              {formatPriceDisplay(prod.defaultPrice)}
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )
            ) : (
              <div className="mt-3 space-y-3">
                <div>
                  <div className="text-xs uppercase text-gray-500 dark:text-gray-300 mb-1">
                    Select Shop
                  </div>
                  {sortedShopList.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-300">
                      No shops available. Create a shop to get started.
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto pr-1">
                      {sortedShopList.map((shop) => {
                        const isActive = selectedSuggestionShopId === shop.id;
                        return (
                          <button
                            key={shop.id}
                            type="button"
                            onClick={() => handleSuggestionShopSelect(shop.id)}
                            className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                              isActive
                                ? 'bg-indigo-600 text-white border-indigo-600'
                                : 'bg-white text-gray-700 border-gray-300 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600'
                            }`}
                          >
                            {shop.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                {selectedSuggestionShop ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-300">
                      <span>Products from {selectedSuggestionShop.name}</span>
                      <button
                        type="button"
                        className={`hover:underline ${
                          loadingShopWiseProducts
                            ? 'opacity-50 cursor-not-allowed'
                            : ''
                        }`}
                        onClick={() =>
                          fetchProductsForShop(selectedSuggestionShop.id, {
                            force: true,
                          })
                        }
                        disabled={loadingShopWiseProducts}
                      >
                        Refresh
                      </button>
                    </div>
                    <input
                      type="text"
                      value={shopWiseSearch}
                      onChange={(e) => setShopWiseSearch(e.target.value)}
                      placeholder="Search products"
                      className="w-full border rounded px-3 py-2 text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                    />
                    <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                      {loadingShopWiseProducts &&
                      !shopWiseProductsForSelectedShop.length ? (
                        <div className="text-sm text-gray-500 dark:text-gray-300">
                          Loading products...
                        </div>
                      ) : filteredShopWiseProducts.length === 0 ? (
                        <div className="text-sm text-gray-500 dark:text-gray-300">
                          {shopWiseProductsForSelectedShop.length === 0
                            ? 'No products available for this shop.'
                            : 'No products match your search.'}
                        </div>
                      ) : (
                        filteredShopWiseProducts.map((prod) => {
                          const variantList = Array.isArray(prod.variants)
                            ? prod.variants
                            : [];
                          const variantCount = variantList.length;
                          const hasVariants = variantCount > 0;
                          const displayPrice = formatPriceDisplay(prod.price);

                          return (
                            <button
                              key={`shop-wise-${prod.shopProductId || prod.productId || prod.name}`}
                              type="button"
                              onClick={() => handleShopWiseProductClick(prod)}
                              className="w-full text-left border rounded px-3 py-2 hover:bg-indigo-50 dark:hover:bg-gray-700 dark:border-gray-600"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="font-medium leading-snug">{prod.name}</div>
                                {(hasVariants || displayPrice || variantCount === 0) && (
                                  <div className="text-right text-[11px] text-gray-500 dark:text-gray-300 space-y-0.5">
                                    {hasVariants
                                      ? variantList.map((variant, idx) => {
                                          const unitLabel = variant.unit || variant.unitLabel || '';
                                          const variantPrice =
                                            variant.price != null
                                              ? formatPriceDisplay(variant.price)
                                              : displayPrice;
                                          const parts = [];
                                          const variantName = variant.name || 'Variant';
                                          parts.push(variantName);
                                          if (unitLabel) {
                                            parts.push(`(${unitLabel})`);
                                          }
                                          if (variantPrice) {
                                            parts.push(`- ${variantPrice}`);
                                          }
                                          const line = parts.join(' ');
                                          if (!line) {
                                            return null;
                                          }
                                          return (
                                            <div
                                              key={
                                                variant.productVariantId ||
                                                variant.id ||
                                                `${prod.productId || prod.shopProductId}-variant-${idx}`
                                              }
                                            >
                                              {line}
                                            </div>
                                          );
                                        })
                                      : (
                                          <>
                                            <div key="variant-count">Variants: 0</div>
                                            {displayPrice && (
                                              <div key="price">Price - {displayPrice}</div>
                                            )}
                                          </>
                                        )}
                                  </div>
                                )}
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-300">
                    Select a shop to view its products.
                  </p>
                )}
              </div>
            )}
          </div>
          {productResults.length > 0 && (
            <div className="border rounded p-2 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold">Search Results</h3>
                <button
                  type="button"
                  className="text-xs text-indigo-600 hover:underline"
                  onClick={() => {
                    setProductResults([]);
                    setProductQuery('');
                  }}
                >
                  Clear
                </button>
              </div>
              <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                {productResults.map((prod) => {
                  const variants = Array.isArray(prod.variants)
                    ? prod.variants
                    : [];
                  const variantCount = variants.length;
                  const fallbackPrice =
                    variantCount === 0
                      ? prod.price
                      : variantCount === 1
                        ? variants[0]?.price
                        : null;
                  return (
                    <button
                      key={`${prod.id || prod.productId || prod.name}`}
                      type="button"
                      onClick={() => handleQuickProductSelect(prod)}
                      className="w-full text-left border rounded px-3 py-2 hover:bg-indigo-50 dark:hover:bg-gray-700 dark:border-gray-600"
                    >
                      <div className="font-medium">{prod.name}</div>
                      <div className="text-xs text-gray-600 dark:text-gray-300">
                        {variantCount > 1
                          ? `${variantCount} variants available`
                          : `Price: ${fallbackPrice ?? '-'}`}
                      </div>
                      <div className="text-[10px] uppercase text-gray-400 mt-1">
                        Click to use in the order form
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="border rounded p-2 dark:border-gray-700">
            <h3 className="font-semibold mb-2">Check Delivery Charge</h3>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                className="w-24 border rounded px-2 py-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="KM"
                value={kmInput}
                onChange={(e) => setKmInput(e.target.value)}
              />
              <div className="font-semibold flex items-center space-x-1">
                <span>
                  {typeof deliveryChargeResult === 'number'
                    ? deliveryChargeResult.toFixed(2)
                    : '--'}
                </span>
                <RefreshCw
                  className="w-4 h-4 cursor-pointer"
                  onClick={() => {
                    if (typeof deliveryChargeResult === 'number') {
                      setDeliveryCharge(deliveryChargeResult.toFixed(2));
                    }
                  }}
                />
              </div>
            </div>
          </div>
          {items.length > 0 && (
            <div>
              <h3 className="font-semibold mb-2">Items by Shop</h3>
              {Object.values(groupedItems).map((group) => (
                <div
                  key={group.shopId}
                  className="border rounded p-2 mb-2 dark:border-gray-700"
                >
                  <h4 className="font-semibold">{group.shopName}</h4>
                  <ul className="list-disc list-inside">
                    {group.items.map((it, idx) => (
                      <li key={idx}>
                        {it.productName} x {it.qty}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {showCustomerForm && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-25 dark:bg-opacity-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 dark:text-white p-4 rounded w-full max-w-md">
            <h2 className="text-xl mb-4">Create Customer</h2>
            <form onSubmit={handleCustomerSubmit} className="space-y-4">
              <div>
                <input
                  type="text"
                  placeholder="Name"
                  className={`border px-3 py-2 rounded w-full ${formErrors.name ? 'border-red-500' : ''} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                  value={customerForm.name}
                  onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })}
                />
                {formErrors.name && <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>}
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Phone"
                  className={`border px-3 py-2 rounded w-full ${formErrors.phone ? 'border-red-500' : ''} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                  value={customerForm.phone}
                  onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })}
                />
                {formErrors.phone && <p className="text-red-500 text-sm mt-1">{formErrors.phone}</p>}
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Address"
                  className={`border px-3 py-2 rounded w-full ${formErrors.address ? 'border-red-500' : ''} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                  value={customerForm.address}
                  onChange={(e) => setCustomerForm({ ...customerForm, address: e.target.value })}
                />
                {formErrors.address && <p className="text-red-500 text-sm mt-1">{formErrors.address}</p>}
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-200 rounded dark:bg-gray-700 dark:text-white"
                  onClick={() => {
                    setShowCustomerForm(false);
                    setFormErrors({});
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddressForm && selectedCustomer && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-25 dark:bg-opacity-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 dark:text-white p-4 rounded w-full max-w-md">
            <h2 className="text-xl mb-4">Add Address</h2>
            <form onSubmit={handleAddressSubmit} className="space-y-4">
              <div>
                <input
                  type="text"
                  placeholder="Address"
                  className={`border px-3 py-2 rounded w-full ${addressFormErrors.address ? 'border-red-500' : ''} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                  value={addressForm.address}
                  onChange={(e) => setAddressForm({ ...addressForm, address: e.target.value })}
                />
                {addressFormErrors.address && (
                  <p className="text-red-500 text-sm mt-1">{addressFormErrors.address}</p>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="addr-default"
                  checked={addressForm.isDefault === '1'}
                  onChange={(e) =>
                    setAddressForm({
                      ...addressForm,
                      isDefault: e.target.checked ? '1' : '0',
                    })
                  }
                />
                <label htmlFor="addr-default">Default</label>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-200 rounded dark:bg-gray-700 dark:text-white"
                  onClick={() => {
                    setShowAddressForm(false);
                    setAddressFormErrors({});
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showShopForm && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-25 dark:bg-opacity-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 dark:text-white p-4 rounded w-full max-w-md">
            <h2 className="text-xl mb-4">Create Shop</h2>
            <form onSubmit={handleShopSubmit} className="space-y-4">
              <div>
                <input
                  type="text"
                  placeholder="Name"
                  className={`border px-3 py-2 rounded w-full ${shopFormErrors.name ? 'border-red-500' : ''} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                  value={shopForm.name}
                  onChange={(e) => setShopForm({ ...shopForm, name: e.target.value })}
                />
                {shopFormErrors.name && <p className="text-red-500 text-sm mt-1">{shopFormErrors.name}</p>}
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Phone"
                  className={`border px-3 py-2 rounded w-full ${shopFormErrors.phone ? 'border-red-500' : ''} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                  value={shopForm.phone}
                  onChange={(e) => setShopForm({ ...shopForm, phone: e.target.value })}
                />
                {shopFormErrors.phone && <p className="text-red-500 text-sm mt-1">{shopFormErrors.phone}</p>}
              </div>
              <div>
                <select
                  value={shopForm.shopCategoryId}
                  onChange={(e) => setShopForm({ ...shopForm, shopCategoryId: e.target.value })}
                  className={`border px-3 py-2 rounded w-full ${shopFormErrors.shopCategoryId ? 'border-red-500' : ''} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                >
                  <option value="">Select Category</option>
                  {shopCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {shopFormErrors.shopCategoryId && (
                  <p className="text-red-500 text-sm mt-1">{shopFormErrors.shopCategoryId}</p>
                )}
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Address"
                  className={`border px-3 py-2 rounded w-full ${shopFormErrors.address ? 'border-red-500' : ''} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                  value={shopForm.address}
                  onChange={(e) => setShopForm({ ...shopForm, address: e.target.value })}
                />
                {shopFormErrors.address && <p className="text-red-500 text-sm mt-1">{shopFormErrors.address}</p>}
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-200 rounded dark:bg-gray-700 dark:text-white"
                  onClick={() => {
                    setShowShopForm(false);
                    setShopFormErrors({});
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showProductForm && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-25 dark:bg-opacity-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 dark:text-white p-4 rounded w-full max-w-md">
            <h2 className="text-xl mb-4">Add Product</h2>
            <form onSubmit={handleProductSubmit} className="space-y-4">
              <div>
                <input
                  type="text"
                  placeholder="Name"
                  className={`border px-3 py-2 rounded w-full ${productFormErrors.name ? 'border-red-500' : ''} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                  value={productForm.name}
                  onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                />
                {productFormErrors.name && <p className="text-red-500 text-sm mt-1">{productFormErrors.name}</p>}
              </div>
              <div>
                <input
                  type="number"
                  placeholder="Price"
                  className={`border px-3 py-2 rounded w-full ${productFormErrors.price ? 'border-red-500' : ''} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                  value={productForm.price}
                  onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                />
                {productFormErrors.price && <p className="text-red-500 text-sm mt-1">{productFormErrors.price}</p>}
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-200 rounded dark:bg-gray-700 dark:text-white"
                  onClick={() => {
                    setShowProductForm(false);
                    setProductFormErrors({});
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </LayoutWithSidebar>
  );
}

